import React, { useState } from 'react';
import { useAuth } from '../../context/Auth';
import Navigation from '../Home/Navigation';
import { getApiUrl } from '../../config/api';
import styles from './SettingsPage.module.css';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleManageBilling = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please log in to manage your billing');
      }

      const response = await fetch(getApiUrl('/billing/create-portal-session'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/settings`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to access billing portal');
      }

      const { portalUrl } = await response.json();
      
      // Redirect to Stripe's customer portal
      window.location.href = portalUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to access billing portal');
    } finally {
      setLoading(false);
    }
  };

  const getPlanDisplayName = (plan: string): string => {
    switch (plan?.toLowerCase()) {
      case 'creator': return '🆓 Creator (Free)';
      case 'creator_lite': return '⚡ Creator Lite';
      case 'creator_pro': return '🚀 Creator Pro';
      case 'creator_studio': return '🎬 Creator Studio';
      case 'creator_unlimited': return '🌟 Creator Unlimited';
      default: return plan?.charAt(0).toUpperCase() + plan?.slice(1).replace(/_/g, ' ') || 'Unknown Plan';
    }
  };

  const hasPaidPlan = user?.plan && user.plan !== 'creator' && user.plan !== 'free';

  return (
    <div className={styles.pageContainer}>
      <Navigation />
      
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>⚙️ Account Settings</h1>
          <p className={styles.subtitle}>Manage your account, subscription, and preferences</p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className={styles.errorBanner}>
            <span className={styles.errorIcon}>⚠️</span>
            <span>{error}</span>
            <button onClick={() => setError('')} className={styles.closeButton}>×</button>
          </div>
        )}

        {success && (
          <div className={styles.successBanner}>
            <span className={styles.successIcon}>✅</span>
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className={styles.closeButton}>×</button>
          </div>
        )}

        {/* Account Information Section */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Account Information</h2>
          
          <div className={styles.infoCard}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Email</span>
              <span className={styles.infoValue}>{user?.email || 'Not available'}</span>
            </div>
            
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Current Plan</span>
              <span className={styles.infoValue}>{getPlanDisplayName(user?.plan || 'creator')}</span>
            </div>

            {/* Payment Information for Paid Plans */}
            {hasPaidPlan && (
              <>
                <div style={{ borderTop: '1px solid rgba(71, 85, 105, 0.3)', margin: '1rem 0' }}></div>
                
                {user?.lastPaymentDate && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>💳 Last Payment</span>
                    <span className={styles.infoValue}>
                      {new Date(user.lastPaymentDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}

                {user?.nextBillingDate && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>📅 Next Billing Date</span>
                    <span className={styles.infoValue}>
                      {new Date(user.nextBillingDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}

                {user?.paymentStatus && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Payment Status</span>
                    <span className={styles.infoValue} style={{
                      color: user.paymentStatus === 'active' ? '#10b981' : 
                             user.paymentStatus === 'past_due' ? '#f59e0b' : '#ef4444',
                      textTransform: 'capitalize'
                    }}>
                      {user.paymentStatus === 'active' && '✓ '}
                      {user.paymentStatus === 'past_due' && '⚠️ '}
                      {user.paymentStatus === 'canceled' && '✗ '}
                      {user.paymentStatus.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Subscription Management Section */}
        {hasPaidPlan && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Subscription Management</h2>
            
            <div className={styles.billingCard}>
              <div className={styles.billingHeader}>
                <div>
                  <h3 className={styles.billingTitle}>💳 Manage Billing</h3>
                  <p className={styles.billingDescription}>
                    Access your billing portal to manage subscriptions, update payment methods, and view invoices
                  </p>
                </div>
              </div>

              <div className={styles.billingFeatures}>
                <div className={styles.feature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Update credit card information</span>
                </div>
                <div className={styles.feature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>View payment history & invoices</span>
                </div>
                <div className={styles.feature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Cancel or modify subscription</span>
                </div>
                <div className={styles.feature}>
                  <span className={styles.featureIcon}>✓</span>
                  <span>Download receipts</span>
                </div>
              </div>

              <button 
                className={styles.billingButton}
                onClick={handleManageBilling}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className={styles.spinner}></div>
                    <span>Opening Portal...</span>
                  </>
                ) : (
                  <>
                    <span className={styles.buttonIcon}>🔧</span>
                    <span>Open Billing Portal</span>
                  </>
                )}
              </button>

              <p className={styles.securityNote}>
                <span className={styles.lockIcon}>🔒</span>
                Secure billing powered by Stripe
              </p>
            </div>
          </section>
        )}

        {/* Plan Actions Section */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Plan & Usage</h2>
          
          <div className={styles.actionsGrid}>
            <div className={styles.actionCard} onClick={() => window.location.href = '/usage'}>
              <div className={styles.actionIcon}>📊</div>
              <h3 className={styles.actionTitle}>View Usage</h3>
              <p className={styles.actionDescription}>
                Check your current usage, points, and feature limits
              </p>
              <div className={styles.actionArrow}>→</div>
            </div>

            <div className={styles.actionCard} onClick={() => window.location.href = '/pricing'}>
              <div className={styles.actionIcon}>⚡</div>
              <h3 className={styles.actionTitle}>Upgrade Plan</h3>
              <p className={styles.actionDescription}>
                Explore plans with more features and higher limits
              </p>
              <div className={styles.actionArrow}>→</div>
            </div>
          </div>
        </section>

        {/* Help Section */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Need Help?</h2>
          
          <div className={styles.helpCard}>
            <p className={styles.helpText}>
              Have questions about your account or billing? We're here to help!
            </p>
            <a 
              href="mailto:info@kingdomstudio.pro" 
              className={styles.contactButton}
            >
              <span className={styles.buttonIcon}>📧</span>
              <span>Contact Support</span>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
